import { useMatches, useRouteLoaderData, type Location } from "@remix-run/react";
import invariant from "invariant";
import type { RootData } from "~/root";

export function useRootLoaderData() {
	const data = useRouteLoaderData("root") as RootData;
	invariant(data, "Root data is not available");
	return data;
}
export function useRouteHandle() {
	const matches = useMatches();
	const currentRoute = matches[matches.length - 1]; // Get the deepest (current) route
	return (currentRoute?.handle as { hideUserPrefs?: boolean; }) || {}; // Return empty object if no handle
}
export function useImageHost() {
	const data = useRouteLoaderData("root") as RootData;
	invariant(data, "Root data is not available");
	return data.env.IMAGES_HOST;
}
function getPaths(name: string) {
	if (!name) {
		return [];
	}
	return name.split(/\.|(\[\d*\])/).reduce((result, segment) => {
		if (typeof segment !== "undefined" && segment !== "" && segment !== "__proto__" && segment !== "constructor" && segment !== "prototype") {
			if (segment.startsWith("[") && segment.endsWith("]")) {
				const index = segment.slice(1, -1);
				result.push(Number(index));
			} else {
				result.push(segment);
			}
		}
		return result;
	}, [] as Array<number | string>);
}

function setValue(target: Record<string, any>, name: string, valueFn: (currentValue?: unknown) => unknown) {
	const paths = getPaths(name);
	const length = paths.length;
	const lastIndex = length - 1;
	let index = -1;
	let pointer = target;
	while (pointer !== null && ++index < length) {
		const key = paths[index];
		const nextKey = paths[index + 1];
		const newValue = index !== lastIndex ? Object.prototype.hasOwnProperty.call(pointer, key) && pointer[key] !== null ? pointer[key] : typeof nextKey === "number" ? [] : {} : valueFn(pointer[key]);
		pointer[key] = newValue;
		pointer = pointer[key];
	}
}

export function getFormDataObject<T>(body: FormData | URLSearchParams) {
	const context: Record<string, unknown> = {};
	for (const [name, next] of body.entries()) {
		setValue(context, name, (prev) => {
			if (!prev) {
				return next;
			} else if (Array.isArray(prev)) {
				return prev.concat(next);
			} else {
				return [prev, next];
			}
		});
	}
	return context as T;
}

export function redirectToLoginUrl(location: Location, searchParams: URLSearchParams) {
	return `/login?returnUrl=${createReturnUrl(location, searchParams)}`;
}
export function redirectToRegister(location: Location, searchParams: URLSearchParams) {
	return `/register?returnUrl=${createReturnUrl(location, searchParams)}`;
}
export function createReturnUrl(location: Location, searchParams: URLSearchParams) {
	const returnUrl = searchParams.get("returnUrl") ?? `${location.pathname}${location.search}`;
	return returnUrl;
}